import { navLinks } from "@/constants/navbar";
import type { MobileMenuDropdownProps } from "@/types/layout-type";
import { Moon, Sun } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

export default function MobileMenuDropdown({
    isOpen,
    isDark,
    languages,
    onClose,
    toggleTheme,
    changeLanguage,
}: MobileMenuDropdownProps) {
    const { t } = useTranslation();
    const { lang } = useParams();
    const baseRoute = `/${lang || "id"}`;

    if (!isOpen) return null;

    return (
        <div className="md:hidden z-40 absolute top-14 left-0 right-0 bg-white dark:bg-dark border-b border-gray-100 dark:border-dark-2 shadow-xl max-h-[calc(100dvh-3.5rem)] overflow-y-auto pointer-events-auto">
            <div className="flex flex-col p-4 gap-2">
                {navLinks(t).map((link) => {
                    const path = `${baseRoute}${link.path}`;
                    let isActive = false;
                    if (path === baseRoute || path === `${baseRoute}/`) {
                        isActive =
                            location.pathname === path ||
                            location.pathname === `${path}/`;
                    } else {
                        isActive = location.pathname.startsWith(path);
                    }
                    return (
                        <Link
                            key={link.label}
                            to={path}
                            onClick={onClose}
                            className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-surface text-gold dark:bg-gold dark:text-dark" : "text-dark dark:text-white hover:bg-gray-50 dark:hover:bg-dark"}`}>
                            {link.label}
                        </Link>
                    );
                })}

                <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-2 flex flex-col gap-4">
                    <div className="flex justify-between items-center px-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t("nav.theme.label")}
                        </span>
                        <button
                            onClick={toggleTheme}
                            className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-dark text-gray-700 dark:text-gray-200 transition-colors">
                            {isDark ? <Sun size={18} /> : <Moon size={18} />}
                            <span className="text-sm font-bold">
                                {isDark
                                    ? t("nav.theme.light")
                                    : t("nav.theme.dark")}
                            </span>
                        </button>
                    </div>

                    <div className="flex justify-between items-center px-2">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                            {t("nav.choose_lang")}
                        </span>
                        <div className="flex gap-2">
                            {languages.map((l) => (
                                <button
                                    key={l.code}
                                    onClick={() => changeLanguage(l.code)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${lang === l.code ? "bg-gold text-white border-gold" : "border-gray-200 dark:border-dark-2 text-gray-600 dark:text-gray-300 hover:border-gold"}`}>
                                    {l.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
